import React, { useEffect, useRef, useState } from 'react';
import { Bell, Menu, X, Search, ShoppingCart, Compass, LayoutDashboard, Users, Wallet, Star, Package, Shield, User as UserIcon, Headphones, Settings as SettingsIcon, LogOut, ChevronDown, MapPin, Bookmark, Layers } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useWishlistContext } from '../../context/WishlistContext';
import { getUnreadCount, subscribeToNotifications } from '../../services/notificationService';
import { motion, AnimatePresence } from 'framer-motion';
import { useConfirm } from '../../context/ConfirmContext';
import UserAvatar from '../common/UserAvatar';

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  danger?: boolean;
}

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { count: cartCount } = useCart();
  const { count: wishlistCount } = useWishlistContext();
  const { confirm } = useConfirm();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [accountHoverOpen, setAccountHoverOpen] = useState(false);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typedText, setTypedText] = useState('');
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const navMenuRef = useRef<HTMLDivElement>(null);

  const fullText = 'Artist Network International';
  const isHome = location.pathname === '/';
  const isExplore = location.pathname.startsWith('/explore') || location.pathname.startsWith('/feed');

  useEffect(() => {
    let index = 0;
    const typingInterval = setInterval(() => {
      if (index <= fullText.length) {
        setTypedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(typingInterval);
      }
    }, 50);

    return () => clearInterval(typingInterval);
  }, []);

  const loadUnreadCount = async () => {
    if (!user) return;
    try {
      const count = await getUnreadCount();
      setUnreadCount(count);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadUnreadCount();
    
    if (user) {
      const unsubscribe = subscribeToNotifications(
        user.id,
        () => {},
        (count) => setUnreadCount(count)
      );
      return () => unsubscribe();
    }
  }, [user]);

  // Close dropdowns when route changes
  useEffect(() => {
    setShowAccountMenu(false);
    setAccountHoverOpen(false);
    setShowNavMenu(false);
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target as Node)) {
        setShowAccountMenu(false);
        setAccountHoverOpen(false);
      }
      if (navMenuRef.current && !navMenuRef.current.contains(e.target as Node)) {
        setShowNavMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowNavMenu(false);
      navigate(`/?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const menuItems: MenuItem[] = [
    ...(user?.isAdmin
      ? [
          { label: 'Admin Panel', path: '/admin', icon: <Shield size={20} /> },
          { label: 'Manage Frames', path: '/admin/frames', icon: <Layers size={20} /> },
        ]
      : []),
    ...(user?.isPromoted
      ? [
          { label: 'Seller Dashboard', path: '/seller/dashboard', icon: <LayoutDashboard size={20} /> },
          { label: 'My Artworks', path: '/seller/products', icon: <Package size={20} /> },
          { label: 'My Revenue', path: '/seller/revenue', icon: <Wallet size={20} /> },
          { label: 'My Store', path: '/seller/store', icon: <UserIcon size={20} /> },
        ]
      : []),
    { label: 'Profile', path: '/profile', icon: <UserIcon size={20} /> },
    { label: 'My Wishlist', path: '/wishlist', icon: <Bookmark size={20} /> },
    { label: 'Orders', path: '/orders', icon: <Package size={20} /> },
    { label: 'My Addresses', path: '/addresses', icon: <MapPin size={20} /> },
    { label: 'Transactions', path: '/transactions', icon: <Wallet size={20} /> },
    { label: 'Referrals', path: '/referrals', icon: <Users size={20} /> },
    { label: 'Promotion', path: '/promotion', icon: <Star size={20} /> },
    { label: 'Customer Support', path: '/support', icon: <Headphones size={20} /> },
    { label: 'Settings', path: '/profile/edit', icon: <SettingsIcon size={20} /> },
    { label: 'Sign Out', path: '', icon: <LogOut size={20} />, danger: true },
  ];

  const handleMenuClick = async (item: MenuItem) => {
    setShowAccountMenu(false);
    setAccountHoverOpen(false);
    if (item.label === 'Sign Out') {
      const confirmed = await confirm({
        title: 'Sign Out',
        message: 'Are you sure you want to sign out of your ANI account?',
        confirmText: 'Sign Out',
      });
      if (confirmed) {
        await signOut();
        navigate('/');
      }
      return;
    }
    navigate(item.path);
  };

  return (
    <>
      <nav className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          
          {/* Logo */}
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center flex-shrink-0 mr-8 gap-3"
          >
            <img src="/Group 21.png" alt="Artist Network International Logo" className="h-12 w-auto object-contain" />
            <span className="text-lg text-gray-600 font-medium tracking-wide hidden sm:block">
              {typedText}
              {typedText.length < fullText.length && (
                <span className="animate-pulse font-light">|</span>
              )}
            </span>
          </button>

          {/* Desktop Search */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8">
            <form onSubmit={handleSearch} className="w-full relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search for Art, Artists or Styles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              />
            </form>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Desktop Quick Links */}
            <Link 
              to="/explore" 
              className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all mr-1 ${
                isExplore
                  ? 'bg-purple-50 text-purple-700 border border-purple-200 shadow-xs'
                  : 'text-gray-600 hover:text-purple-600 hover:bg-gray-100'
              }`}
            >
              <Compass size={17} className={isExplore ? 'text-purple-600' : 'text-gray-500'} />
              <span>Explore</span>
              <span className="text-[10px] bg-gradient-to-r from-purple-600 to-pink-500 text-white px-1.5 py-0.2 rounded-full font-bold uppercase tracking-wider">
                Reels
              </span>
            </Link>

            {user ? (
              <>
                {/* Wishlist Quick Icon (Desktop) */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/wishlist')}
                  aria-label={`Wishlist, ${wishlistCount} items`}
                  title="My Wishlist"
                  className="hidden md:flex p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600 hover:text-purple-600 relative"
                >
                  <Bookmark size={21} />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[19px] h-[19px] bg-rose-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow-sm px-1">
                      {wishlistCount > 9 ? '9+' : wishlistCount}
                    </span>
                  )}
                </motion.button>

                {/* Shopping Cart Icon (Visible on BOTH Mobile & Desktop) */}
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/cart')}
                  aria-label={`Cart, ${cartCount} items`}
                  title="Cart"
                  className="flex p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600 hover:text-purple-600 relative"
                >
                  <ShoppingCart size={22} />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[19px] h-[19px] bg-indigo-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow-sm px-1">
                      {cartCount > 9 ? '9+' : cartCount}
                    </span>
                  )}
                </motion.button>

                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/notifications')}
                  aria-label="Notifications"
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors relative text-gray-600 hover:text-purple-600"
                >
                  <Bell size={22} />
                  {unreadCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </motion.span>
                  )}
                </motion.button>

                {/* Account / Profile Dropdown Menu */}
                <div
                  ref={accountMenuRef}
                  className="relative"
                  onMouseEnter={() => setAccountHoverOpen(true)}
                  onMouseLeave={() => setAccountHoverOpen(false)}
                >
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowAccountMenu((s) => !s);
                      setShowNavMenu(false);
                    }}
                    aria-label="Account menu"
                    className="flex items-center gap-1.5 p-1.5 pl-2 hover:bg-gray-100 rounded-full transition-colors border border-transparent hover:border-gray-200"
                  >
                    <UserAvatar name={user.name} avatar={user.avatar || ''} size={32} className="ring-2 ring-purple-100" />
                    <ChevronDown size={16} className={`text-gray-500 transition-transform ${showAccountMenu || accountHoverOpen ? 'rotate-180' : ''}`} />
                  </motion.button>

                  <AnimatePresence>
                    {(showAccountMenu || accountHoverOpen) && (
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden w-72"
                      >
                        {/* User header */}
                        <div className="px-5 py-4 bg-gradient-to-br from-purple-50 to-blue-50 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            <UserAvatar name={user.name} avatar={user.avatar || ''} size={44} className="ring-2 ring-white" />
                            <div className="min-w-0">
                              <p className="font-bold text-gray-900 truncate">{user.name}</p>
                              <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                          </div>
                        </div>

                        <div className="py-1.5 max-h-[60vh] overflow-y-auto">
                          {menuItems.map((item) => (
                            <button
                              key={item.label}
                              onClick={() => handleMenuClick(item)}
                              className={`w-full px-5 py-3 flex items-center gap-3 transition-colors text-left ${
                                item.danger
                                  ? 'text-red-600 hover:bg-red-50 border-t border-gray-100 mt-1 font-semibold'
                                  : 'text-gray-700 hover:bg-purple-50'
                              }`}
                            >
                              <span className={item.danger ? 'text-red-500' : ''}>{item.icon}</span>
                              <span className="font-medium">{item.label}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile Navigation Drawer Toggle */}
                <div ref={navMenuRef} className="md:hidden">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setShowNavMenu((s) => !s);
                      setShowAccountMenu(false);
                    }}
                    aria-label="Toggle navigation menu"
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-800"
                  >
                    {showNavMenu ? <X size={24} /> : <Menu size={24} />}
                  </motion.button>
                </div>
              </>
            ) : (
              <>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/cart')}
                  aria-label="Cart"
                  className="flex p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600 hover:text-purple-600 relative"
                >
                  <ShoppingCart size={22} />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[19px] h-[19px] bg-indigo-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow-sm px-1">
                      {cartCount}
                    </span>
                  )}
                </motion.button>

                <div ref={navMenuRef} className="md:hidden">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowNavMenu((s) => !s)}
                    aria-label="Toggle menu"
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-800"
                  >
                    {showNavMenu ? <X size={24} /> : <Menu size={24} />}
                  </motion.button>
                </div>

                <button
                  onClick={() => navigate('/signin')}
                  className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full font-semibold shadow-md hover:shadow-lg transition-all text-sm"
                >
                  Sign In
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Navigation Drawer / Dropdown */}
        <AnimatePresence>
          {showNavMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden border-t border-gray-100 bg-white px-4 pt-3 pb-5 space-y-4 shadow-xl"
            >
              {/* Mobile Search Input */}
              <form onSubmit={handleSearch} className="w-full relative">
                <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={17} />
                <input
                  type="text"
                  placeholder="Search art, artists, styles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </form>

              {/* Navigation Grid */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  onClick={() => { setShowNavMenu(false); navigate('/explore'); }}
                  className="flex items-center gap-2.5 p-3 rounded-2xl hover:bg-purple-50 bg-purple-50/50 text-purple-900 font-semibold text-sm text-left transition-colors border border-purple-100"
                >
                  <Compass size={18} className="text-purple-600" />
                  <span>ANI Explore</span>
                  <span className="text-[9px] bg-purple-600 text-white px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ml-auto">Reels</span>
                </button>
                <button
                  onClick={() => { setShowNavMenu(false); navigate('/wishlist'); }}
                  className="flex items-center gap-2.5 p-3 rounded-2xl hover:bg-purple-50 bg-gray-50 text-gray-800 font-semibold text-sm text-left transition-colors"
                >
                  <Bookmark size={18} className="text-indigo-600" />
                  <span>My Wishlist ({wishlistCount})</span>
                </button>
                <button
                  onClick={() => { setShowNavMenu(false); navigate('/cart'); }}
                  className="flex items-center gap-2.5 p-3 rounded-2xl hover:bg-purple-50 bg-gray-50 text-gray-800 font-semibold text-sm text-left transition-colors"
                >
                  <ShoppingCart size={18} className="text-emerald-600" />
                  <span>Cart ({cartCount})</span>
                </button>
                {user?.isPromoted ? (
                  <button
                    onClick={() => { setShowNavMenu(false); navigate('/seller/dashboard'); }}
                    className="flex items-center gap-2.5 p-3 rounded-2xl hover:bg-purple-50 bg-blue-50 text-blue-900 font-semibold text-sm text-left transition-colors"
                  >
                    <LayoutDashboard size={18} className="text-blue-600" />
                    <span>Seller Portal</span>
                  </button>
                ) : (
                  <button
                    onClick={() => { setShowNavMenu(false); navigate('/seller/join'); }}
                    className="flex items-center gap-2.5 p-3 rounded-2xl hover:bg-purple-50 bg-purple-50 text-purple-900 font-semibold text-sm text-left transition-colors"
                  >
                    <Star size={18} className="text-amber-500" />
                    <span>Become a Seller</span>
                  </button>
                )}
              </div>

              {/* Informational Links */}
              <div className="pt-2 border-t border-gray-100 flex flex-wrap items-center justify-between text-xs text-gray-500 px-1">
                <Link to="/about" onClick={() => setShowNavMenu(false)} className="hover:text-purple-600 py-1">About ANI</Link>
                <Link to="/contact" onClick={() => setShowNavMenu(false)} className="hover:text-purple-600 py-1">Contact Us</Link>
                <Link to="/terms" onClick={() => setShowNavMenu(false)} className="hover:text-purple-600 py-1">Terms & Conditions</Link>
                <Link to="/platform-fees" onClick={() => setShowNavMenu(false)} className="hover:text-purple-600 py-1">Platform Fees</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};
